import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface WidgetInstance {
  id: string;
  type: string;
  title: string;
  size: '1x1' | '2x1' | '2x2';
  position: { x: number; y: number };
  config?: Record<string, any>;
}

export interface Dashboard {
  id: string;
  name: string;
  scope: 'org' | 'project';
  projectId?: string;
  layout: {
    columns: number;
    gap: number;
  };
  widgets: WidgetInstance[];
}

interface WidgetsState {
  dashboards: Dashboard[];
  activeDashboardId: string | null;
}

const initialState: WidgetsState = {
  dashboards: [
    {
      id: 'default-org',
      name: 'Organization Overview',
      scope: 'org',
      layout: { columns: 12, gap: 16 },
      widgets: [],
    },
  ],
  activeDashboardId: 'default-org',
};

const widgetsSlice = createSlice({
  name: 'widgets',
  initialState,
  reducers: {
    addDashboard: (state, action: PayloadAction<Omit<Dashboard, 'id' | 'widgets'>>) => {
      const newDashboard: Dashboard = {
        ...action.payload,
        id: `dashboard-${Date.now()}`,
        widgets: [],
      };
      state.dashboards.push(newDashboard);
    },

    renameDashboard: (state, action: PayloadAction<{ id: string; name: string }>) => {
      const dashboard = state.dashboards.find(d => d.id === action.payload.id);
      if (dashboard) {
        dashboard.name = action.payload.name;
      }
    },

    deleteDashboard: (state, action: PayloadAction<string>) => {
      const index = state.dashboards.findIndex(d => d.id === action.payload);
      if (index !== -1) {
        state.dashboards.splice(index, 1);
        // If deleted dashboard was active, switch to first available
        if (state.activeDashboardId === action.payload) {
          state.activeDashboardId = state.dashboards[0]?.id || null;
        }
      }
    },

    setActiveDashboard: (state, action: PayloadAction<string>) => {
      state.activeDashboardId = action.payload;
    },

    upsertWidget: (
      state,
      action: PayloadAction<{ dashboardId: string; widget: WidgetInstance }>
    ) => {
      const dashboard = state.dashboards.find(d => d.id === action.payload.dashboardId);
      if (dashboard) {
        const existingIndex = dashboard.widgets.findIndex(w => w.id === action.payload.widget.id);
        if (existingIndex !== -1) {
          dashboard.widgets[existingIndex] = action.payload.widget;
        } else {
          dashboard.widgets.push(action.payload.widget);
        }
      }
    },

    removeWidget: (state, action: PayloadAction<{ dashboardId: string; widgetId: string }>) => {
      const dashboard = state.dashboards.find(d => d.id === action.payload.dashboardId);
      if (dashboard) {
        dashboard.widgets = dashboard.widgets.filter(w => w.id !== action.payload.widgetId);
      }
    },

    reorderWidgets: (
      state,
      action: PayloadAction<{ dashboardId: string; widgets: WidgetInstance[] }>
    ) => {
      const dashboard = state.dashboards.find(d => d.id === action.payload.dashboardId);
      if (dashboard) {
        dashboard.widgets = action.payload.widgets;
      }
    },

    updateWidgetPosition: (
      state,
      action: PayloadAction<{
        dashboardId: string;
        widgetId: string;
        position: { x: number; y: number };
      }>
    ) => {
      const dashboard = state.dashboards.find(d => d.id === action.payload.dashboardId);
      if (dashboard) {
        const widget = dashboard.widgets.find(w => w.id === action.payload.widgetId);
        if (widget) {
          widget.position = action.payload.position;
        }
      }
    },

    updateWidgetConfig: (
      state,
      action: PayloadAction<{
        dashboardId: string;
        widgetId: string;
        config: Record<string, any>;
      }>
    ) => {
      const dashboard = state.dashboards.find(d => d.id === action.payload.dashboardId);
      if (dashboard) {
        const widget = dashboard.widgets.find(w => w.id === action.payload.widgetId);
        if (widget) {
          widget.config = { ...widget.config, ...action.payload.config };
        }
      }
    },

    initializeProjectDashboard: (state, action: PayloadAction<string>) => {
      const projectId = action.payload;
      const existingDashboard = state.dashboards.find(
        d => d.scope === 'project' && d.projectId === projectId
      );

      if (!existingDashboard) {
        const newDashboard: Dashboard = {
          id: `project-${projectId}`,
          name: 'Project Dashboard',
          scope: 'project',
          projectId,
          layout: { columns: 12, gap: 16 },
          widgets: [],
        };
        state.dashboards.push(newDashboard);
      }
    },
  },
});

export const {
  addDashboard,
  renameDashboard,
  deleteDashboard,
  setActiveDashboard,
  upsertWidget,
  removeWidget,
  reorderWidgets,
  updateWidgetPosition,
  updateWidgetConfig,
  initializeProjectDashboard,
} = widgetsSlice.actions;

// Selectors
export const selectAllDashboards = (state: { widgets: WidgetsState }) => state.widgets.dashboards;
export const selectActiveDashboardId = (state: { widgets: WidgetsState }) =>
  state.widgets.activeDashboardId;
export const selectActiveDashboard = (state: { widgets: WidgetsState }) => {
  const activeDashboardId = state.widgets.activeDashboardId;
  return state.widgets.dashboards.find(d => d.id === activeDashboardId) || null;
};
export const selectDashboardById = (state: { widgets: WidgetsState }, dashboardId: string) =>
  state.widgets.dashboards.find(d => d.id === dashboardId) || null;
export const selectProjectDashboard = (state: { widgets: WidgetsState }, projectId: string) =>
  state.widgets.dashboards.find(d => d.scope === 'project' && d.projectId === projectId) || null;
export const selectOrgDashboards = (state: { widgets: WidgetsState }) =>
  state.widgets.dashboards.filter(d => d.scope === 'org');

export default widgetsSlice.reducer;
